import { differenceInHours, format } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import bigAssBattery from "../../assets/BigAssBattery.svg";
import cat950Icon from "../../assets/Cat950.png";
import chargingStationMapIcon from "../../assets/ChargingStationMapIcon.svg";
import densBattery from "../../assets/DensBattery.svg";
import MediumWheelLoaderIcon from "../../assets/MediumWheelLoader.jpg";
import pswIcon from "../../assets/PswIcon.svg";
import xosHubIcon from "../../assets/XOS_Hub.png";
import chargerIcon from "../../assets/charger.jpg";
import defaultMarkerIcon from "../../assets/defaultMarkerIcon.png";
import methanolReformerIcon from "../../assets/methanolReformer.png";
import unknownMachineIcon from "../../assets/unknownMachine.svg";
import { NodeIconColor } from "../../components/CustomNodes/CustomNodeEnums";
import { SANKEY_NODE_WIDTH, SANKEY_SVG_WIDTH } from "../../components/SankeyDiagram/SankeyDiagram.interface";
import { isIndianaChargerStation } from "../../hooks/useIsIndianaChargerStationFlag";
import { MONTH_NAMES, formatTimestampWithTimezone } from "../../shared/timestampHelpers";
import { Activity } from "../../swagger/caasService-v1/index";
import { AssetStatus } from "../../swagger/caasService-v1/index";
import { NetworkColumn, createLaneAllocator, getLayoutRule, resolveAssetPosition, resolveNodeIcon } from "./networkDiagramLayout";
export { AssetTransitDirection } from "./networkDiagramLayout";
// Constants for asset makes
const DENS_MAKE = "DENS";
const PSW_MAKE = "PSW";
const BAB_MAKE = "BAB";
// Constants for asset product family codes
const MDWL_PRODUCT_FAMILY = "MDWL";
// Constants for asset models
const MODEL_950GC = "950GC";
/**
 * Asset source types returned by the API.
 *
 * ENERGY_SOURCE is the generic energy-source category (methanol reformer).
 * The legacy "METHANOL REFORMER" string is no longer recognised.
 */
export const AssetSourceType = {
    CHARGER: "CHARGER",
    CHARGING_STATION: "CHARGING_STATION",
    DIESEL_GENERATOR: "DIESEL_GENERATOR",
    ENERGY_SOURCE: "ENERGY_SOURCE",
    ESS: "ESS",
    GHOST: "GHOST",
    MACHINE: "MACHINE"
};
// API casing is not guaranteed, so every comparison goes through this.
const normalizeAssetType = (assetSourceType) => assetSourceType?.toUpperCase() ?? "";
const isAssetType = (assetSourceType, type) => normalizeAssetType(assetSourceType) === type;
export const isCharger = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.CHARGER);
export const isChargingStation = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.CHARGING_STATION);
export const isDieselGenerator = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.DIESEL_GENERATOR);
/** Methanol reformer and any other generic energy source. */
export const isEnergySource = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.ENERGY_SOURCE);
/** Battery / energy storage system. */
export const isEss = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.ESS);
/** Unknown ("ghost") machine inferred from a charging session. */
export const isGhost = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.GHOST);
export const isMachine = (assetSourceType) => isAssetType(assetSourceType, AssetSourceType.MACHINE);
/**
 * Per-type presentation settings. Add a row here rather than another branch in
 * getAssetImageIcon / prepareSankeyChartNodes.
 */
const ASSET_TYPE_CONFIG = {
    [AssetSourceType.CHARGER]: { icon: chargerIcon, sankeyNodeType: "CHARGER" },
    [AssetSourceType.CHARGING_STATION]: { sankeyNodeType: "CHARGING_STATION" },
    [AssetSourceType.ENERGY_SOURCE]: { icon: methanolReformerIcon, sankeyNodeType: "METHANOL_REFORMER" },
    [AssetSourceType.GHOST]: { icon: unknownMachineIcon, sankeyNodeType: AssetSourceType.GHOST }
};
// ESS battery icon depends on the product family code, not the source type alone.
const ESS_PRODUCT_FAMILY_ICONS = {
    [BAB_MAKE]: bigAssBattery,
    [DENS_MAKE]: densBattery
};
// Assets with a hard-coded Cat 950 icon override.
const CAT_950_ASSET_IDS = new Set([
    "9fd64572-d32a-4473-951a-5d16a053646e",
    "5fe212ea-8fa8-4d71-a020-d3e4b504ee3a",
    "0fa9aedd-8b63-4436-b17f-f88fc4994290"
]);
// XOS (6TA) charger asset IDs — used in getNetworkNodes and getAssetImageIcon
const XOS_CHARGER_ASSET_IDS = ["815dc59c-3819-4a77-95a5-2397c6321060", "037e557d-8d19-4680-a958-c1845affbbb9"];
/**
 * Checks if a timestamp string is in time-only format (HH:mm:ss).
 *
 * Time-only format is used by some projects (e.g., Malaga) where the API returns
 * only the time portion without a date (e.g., "15:00:00", "07:00:00").
 *
 * @param timestamp - The timestamp string to check
 * @returns true if the timestamp matches the time-only format (HH:mm:ss), false otherwise
 *
 * @example
 * isTimeOnlyTimestamp("15:00:00") // true
 * isTimeOnlyTimestamp("2025-12-18T15:00:00") // false
 * isTimeOnlyTimestamp("15:00") // false (missing seconds)
 * isTimeOnlyTimestamp(null) // false
 */
export const isTimeOnlyTimestamp = (timestamp) => {
    if (!timestamp)
        return false;
    return /^\d{2}:\d{2}:\d{2}$/.test(timestamp);
};
// Asset name mappings for special cases (e.g., DENS assets with non-standard naming)
const ASSET_NAME_MAPPINGS = {
    E_NOMAD_01: "E_N_1",
    E_NOMAD_02: "E_N_2",
    "PH10-24016": "PH16",
    "PH10-24019": "PH19"
};
const UNKNOWN_ASSET_PREFIX = "Unknown Asset";
const formatUnknownAssetName = (assetName) => {
    if (assetName?.toLocaleLowerCase()?.startsWith(UNKNOWN_ASSET_PREFIX?.toLocaleLowerCase())) {
        const suffix = assetName?.substring(UNKNOWN_ASSET_PREFIX.length)?.trim();
        return suffix;
    }
    return null;
};
/**
 * Returns the appropriate battery icon based on the make field
 */
export const getBatteryIconByMake = (make) => {
    switch (make) {
        case PSW_MAKE:
            return pswIcon;
        case DENS_MAKE:
            return densBattery;
        case BAB_MAKE:
        default:
            return bigAssBattery;
    }
};
/**
 * Returns the display name for a battery make
 */
export const getBatteryMakeDisplayName = (make) => {
    switch (make) {
        case PSW_MAKE:
            return "Pon Energy Rental";
        case BAB_MAKE:
            return "Big Ass Battery";
        case DENS_MAKE:
            return "DENS Battery";
        default:
            return make;
    }
};
// X coordinate of the "Work Area" site-machine/site-ghost column (machines charging AtSite).
const SITE_MACHINE_COLUMN_X = NetworkColumn.WORK_AREA;
/**
 * Reorder the Work Area (site machine / site ghost) column so each node's vertical order
 * follows its charging source's vertical position. Nodes are re-slotted only into the set of
 * y positions they already occupy, so the diagram footprint (and fit-to-view) is unchanged —
 * this only removes edge crossings caused by a machine being stacked below a peer whose
 * charging source actually sits higher up. Sorting a single-parent target column by parent
 * position is the crossing-minimal ordering, so lines take the most direct route.
 */
export const reorderSiteMachinesBySource = (networkNodes, chargingSourceByAssetId) => {
    const machineNodes = networkNodes.filter((node) => node.type === "assetNode" && node.position.x === SITE_MACHINE_COLUMN_X);
    if (machineNodes.length < 2)
        return;
    const yByNodeId = new Map(networkNodes.map((node) => [node.id, node.position.y]));
    // The exact y-slots currently used by this column, ascending — reused as-is so nothing moves
    // horizontally or grows the canvas.
    const slots = machineNodes.map((node) => node.position.y).sort((first, second) => first - second);
    const sourceY = (node) => {
        const source = chargingSourceByAssetId.get(node.id);
        const resolved = source ? yByNodeId.get(source) : undefined;
        // Nodes with no resolvable source (null/OTHERS/off-canvas) sink to the bottom, keeping
        // their relative order (Array.prototype.sort is stable).
        return resolved ?? Number.POSITIVE_INFINITY;
    };
    const ordered = [...machineNodes].sort((first, second) => sourceY(first) - sourceY(second));
    ordered.forEach((node, index) => {
        node.position = { ...node.position, y: slots[index] };
    });
};
const hasFreshTelemetry = (data, projectTimezone) => data?.location?.lastReportedTime
    ? isAssetReporting(data.location.lastReportedTime, projectTimezone)
    : !!data?.activity;
const resolveNodeIconColor = (data, tooltipShowOnlyAssetName, projectTimezone) => {
    // Replay mode: empty activity means nothing was recorded at this slider position.
    if (tooltipShowOnlyAssetName) {
        return data?.activity ? getNodeIconColor(data.activity, data?.assetStatus) : NodeIconColor.NOT_REPORTING;
    }
    // Chargers report state changes event-driven rather than on a heartbeat, so skip staleness.
    if (isCharger(data?.assetSourceType)) {
        return getNodeIconColor(data?.activity, data?.assetStatus);
    }
    return hasFreshTelemetry(data, projectTimezone)
        ? getNodeIconColor(data?.activity, data?.assetStatus)
        : NodeIconColor.NOT_REPORTING;
};
const resolveIsReporting = (data, tooltipShowOnlyAssetName, projectTimezone) => {
    if (tooltipShowOnlyAssetName)
        return !!data?.activity;
    // Mirrors resolveNodeIconColor: both inputs to the charger colour count as reporting.
    if (isCharger(data?.assetSourceType))
        return !!(data?.activity || data?.assetStatus);
    return hasFreshTelemetry(data, projectTimezone);
};
export const getNetworkNodes = (assetData, projectName, projectId, projectDetails, assetSoCPowerTrendsDetails, tooltipShowOnlyAssetName, projectTimezone) => {
    const networkNodes = [];
    const lanes = createLaneAllocator();
    const chargeStationPositionX = NetworkColumn.CHARGING_STATION;
    // assetId -> chargingSource id, so the Work Area column can be reordered by source position.
    const chargingSourceByAssetId = new Map();
    (assetData || []).forEach((data) => {
        if (data?.assetId && data?.chargingSource) {
            chargingSourceByAssetId.set(data.assetId, data.chargingSource);
        }
    });
    const sortedData = sortNetworkData(assetData || []);
    const chargeStationData = [];
    sortedData.forEach((data) => {
        const rule = getLayoutRule(data?.assetSourceType);
        if (rule.kind === "station") {
            chargeStationData.push(data);
            return;
        }
        const isTransporting = data?.activity === "Transporting";
        const position = resolveAssetPosition({
            assetId: data?.assetId,
            chargingSource: data?.chargingSource,
            isTransporting,
            transitDirection: data?.assetTransitDirection
        }, rule, lanes);
        const assetIcon = resolveNodeIcon({
            hasWheelLoaderFamily: !!projectDetails?.find((item) => item?.assetId === data?.assetId && item?.assetMetadata?.productFamily?.code === "MDWL"),
            isCharger: isCharger(data?.assetSourceType),
            isIdle: data?.activity === "Idle",
            isMachine: isMachine(data?.assetSourceType),
            isTransporting,
            isXosCharger: XOS_CHARGER_ASSET_IDS.includes(data?.assetId)
        }, rule);
        const xc = position?.x;
        const yc = position?.y;
        networkNodes.push({
            data: {
                activity: data?.activity,
                assetTransitDirection: projectName === "Hansweert" && projectId === "308a84bb-fc3a-442e-92c9-5508086385f0"
                    ? undefined
                    : data?.assetTransitDirection,
                hasSource: isEss(data?.assetSourceType) ||
                    isCharger(data?.assetSourceType) ||
                    isEnergySource(data?.assetSourceType),
                hasTarget: !!data?.chargingSource,
                iconColor: resolveNodeIconColor(data, tooltipShowOnlyAssetName, projectTimezone),
                iconType: assetIcon,
                imageUrl: getAssetImageIcon(data),
                label: getAssetName(data?.assetName, data?.assetSourceType),
                selected: false,
                toolTip: {
                    address: data?.location?.streetAddress,
                    assetDetailsLink: "/asset-page-details/" + data?.assetId,
                    assetEnergy: data?.assetEnergy,
                    assetLatestPower: data?.assetLatestPower,
                    assetName: data?.assetName,
                    // Chargers may report only a status; consumed by the refreshed layout alone.
                    assetStatus: data?.assetStatus,
                    chargeTime: data?.estimatedRemainingChargeTime,
                    distance: data?.odometerReading,
                    distanceUom: "Miles",
                    hours: data?.lifetimeRunningHours,
                    isReporting: resolveIsReporting(data, tooltipShowOnlyAssetName, projectTimezone),
                    powerTrend: trendData(assetSoCPowerTrendsDetails, data.assetId, "power"),
                    soc: data?.soc,
                    socTrend: trendData(assetSoCPowerTrendsDetails, data.assetId, "soc"),
                    state: data?.activity,
                    tooltipShowOnlyAssetName
                }
            },
            id: data?.assetId,
            position: { x: xc, y: yc },
            type: "assetNode"
        });
    });
    if (networkNodes.length !== 0) {
        const indianaChargingStation = chargeStationData.find((data) => isIndianaChargerStation(data?.assetName, data?.assetSourceType));
        const dieselGenerator = (assetData || []).find((data) => isDieselGenerator(data?.assetSourceType));
        networkNodes.push({
            data: {
                assetName: dieselGenerator?.assetName ?? indianaChargingStation?.assetName,
                assetSourceType: dieselGenerator?.assetSourceType ?? indianaChargingStation?.assetSourceType
            },
            id: "Power",
            position: { x: NetworkColumn.POWER, y: 288 },
            type: "utilityPowerNode"
        });
        const getChargeStationLabel = (assetName) => {
            if (projectName === "Amsterdam" && projectId === "d85bd393-6cc3-4461-bcf0-78365d5bbf12") {
                return "Grid Connection";
            }
            const words = assetName?.replace(/_/g, " ").split(" ");
            const capitalizedWords = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
            return capitalizedWords.join(" ");
        };
        chargeStationData.forEach((data) => {
            networkNodes.push({
                data: { label: getChargeStationLabel(data?.assetName) },
                id: data.assetId,
                position: { x: chargeStationPositionX, y: lanes.take("station") },
                type: "chargeStationNode"
            });
        });
    }
    // Order the Work Area column by charging-source position so connection lines don't cross.
    reorderSiteMachinesBySource(networkNodes, chargingSourceByAssetId);
    return networkNodes;
};
const trendData = (assetSoCPowerTrendsDetails, assetId, trend) => {
    const values = [];
    assetSoCPowerTrendsDetails?.forEach((assetTrend) => {
        if (assetTrend.assetId === assetId && trend === "soc") {
            assetTrend.trends?.forEach((trend) => {
                if (trend.soc !== undefined) {
                    values.push(trend.soc);
                }
            });
        }
        else if (assetTrend.assetId === assetId && trend === "power") {
            assetTrend.trends?.forEach((trend) => {
                if (trend.power !== undefined) {
                    values.push(trend.power);
                }
            });
        }
    });
    return values;
};
const sortNetworkData = (assetData) => {
    const providerData = [];
    const methanolData = [];
    const chargingMachines = [];
    const otherData = [];
    assetData.forEach((data) => {
        if (getLayoutRule(data.assetSourceType).providesCharging) {
            providerData.push(data);
        }
        else if (isEnergySource(data?.assetSourceType)) {
            // ENERGY_SOURCE (methanol reformer) assets go after ESS so they
            // stack below ESS nodes in the source column. Prod sort order unchanged.
            methanolData.push(data);
        }
        else if (isMachine(data.assetSourceType) &&
            data.chargingSource !== null &&
            data.chargingSource.toUpperCase() !== "OTHERS") {
            chargingMachines.push(data);
        }
        else if (isGhost(data.assetSourceType) && data.chargingSource !== null && data.chargingSource !== undefined) {
            chargingMachines.push(data);
        }
        else {
            otherData.push(data);
        }
    });
    return providerData.concat(methanolData).concat(chargingMachines).concat(otherData);
};
export const getNodeIconColor = (state, assetStatus) => {
    // Methanol-reformer specific states come from assetStatus, not activity.
    // Check status first so it takes priority over any fallback activity value.
    if (assetStatus === AssetStatus.FAULT) {
        return NodeIconColor.FAULTED;
    }
    if (assetStatus === AssetStatus.OFF) {
        return NodeIconColor.OFF;
    }
    // Charger-specific statuses (may arrive via assetStatus or activity fallback).
    // Normalise to lowercase for case-insensitive comparison.
    const chargerStatus = (assetStatus || state || "").toLowerCase();
    if (chargerStatus === "available") {
        return NodeIconColor.CHARGING; // green — charger is ready/available
    }
    if (chargerStatus === "occupied") {
        return NodeIconColor.OCCUPIED; // #0066FF — matches asset portfolio pill
    }
    if (chargerStatus === "inoperable") {
        return NodeIconColor.FAULTED; // dark red — charger is out of service
    }
    if (state === Activity.CHARGING || state === AssetStatus.AC_CHARGING || state === AssetStatus.DC_CHARGING) {
        return NodeIconColor.CHARGING;
    }
    if (state === Activity.DISCHARGING) {
        return NodeIconColor.DISCHARGING;
    }
    if (state === Activity.IDLE) {
        return NodeIconColor.IDLE;
    }
    if (state === Activity.TRANSPORTING) {
        return NodeIconColor.TRANSPORTING;
    }
    if (state === AssetStatus.TRICKLE) {
        return NodeIconColor.TRICKLE;
    }
    if (state === AssetStatus.SELF_DISCHARGE) {
        return NodeIconColor.SELF_DISCHARGE;
    }
    if (state === AssetStatus.WORKING) {
        return NodeIconColor.WORKING;
    }
    return NodeIconColor.INACTIVE;
};
/**
 * Determines if an asset is currently reporting based on its last reported timestamp.
 *
 * CALCULATION FUNCTION - Uses fromZonedTime approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * NOTE: The API provides timestamps already in the project's local timezone.
 * This function needs accurate UTC milliseconds to compare with Date.now().
 *
 * Why fromZonedTime instead of Z-appending:
 *   - Z-appending would treat "20:46 London" as "20:46 UTC" (WRONG)
 *   - fromZonedTime correctly interprets "20:46 London" and converts to UTC
 *   - This ensures all users get the same "REPORTING" vs "NOT REPORTING" status
 *
 * @param time - Timestamp string in project's local timezone (e.g., "2025-12-17T20:46:00.000")
 * @param projectTimezone - IANA timezone name (e.g., "Europe/London", "Europe/Amsterdam")
 * @returns true if the asset reported within the last 18 hours, false otherwise
 */
const isAssetReporting = (time, projectTimezone) => {
    const MILLI_SECONDS_IN_HOUR = 3600000;
    const HOURS = 18;
    if (!time)
        return false;
    try {
        // Special handling for time-only format (e.g., "19:00:00")
        // Append today's date in the project's timezone to construct a full timestamp
        if (isTimeOnlyTimestamp(time)) {
            if (!projectTimezone || !isValidIANATimezone(projectTimezone)) {
                // Cannot determine staleness without valid timezone, assume reporting
                return true;
            }
            // Get today's date in the project's timezone (e.g., "2025-12-18" in Europe/Amsterdam)
            const todayInProjectTz = formatInTimeZone(new Date(), projectTimezone, "yyyy-MM-dd");
            // Construct full timestamp: "2025-12-18T19:00:00"
            const fullTimestamp = `${todayInProjectTz}T${time}`;
            // Parse as project timezone and convert to UTC for comparison
            const utcDate = fromZonedTime(fullTimestamp, projectTimezone);
            const machineTime = utcDate.getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            return hoursDelta >= 0 && hoursDelta < HOURS;
        }
        // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 London time)
        // We interpret this timestamp AS IF it's in the project's timezone, then convert to UTC for comparison
        if (projectTimezone && isValidIANATimezone(projectTimezone)) {
            // Parse the timestamp string as a date in the project's timezone, convert to UTC
            // fromZonedTime interprets a plain date string as if it's in the specified timezone
            // Returns a Date object with correct UTC milliseconds for reliable comparison
            const utcDate = fromZonedTime(time, projectTimezone);
            const machineTime = utcDate.getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            // Allow up to MAX_UTC_OFFSET_HOURS negative to guard against any clock skew
            // or edge cases where fromZonedTime produces a slightly "future" result.
            const MAX_UTC_OFFSET_HOURS = 14;
            return hoursDelta >= -MAX_UTC_OFFSET_HOURS && hoursDelta < HOURS;
        }
        else {
            // Fallback: treat as UTC if no valid IANA timezone provided.
            // Timestamps are in project local time, not UTC. Appending "Z" makes them
            // appear up to MAX_UTC_OFFSET_HOURS ahead of real UTC for UTC+ projects
            // (e.g. Amsterdam UTC+2 means local 07:00 looks like 07:00Z but actual UTC
            // is 05:00, giving hourDiff = -2). Allow up to 14 hours negative (the world's
            // maximum UTC+ offset) so these assets are not falsely marked Not Reporting.
            const MAX_UTC_OFFSET_HOURS = 14;
            const timeAsUTC = time.endsWith("Z") ? time : `${time}Z`;
            const machineTime = new Date(timeAsUTC).getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            return hoursDelta >= -MAX_UTC_OFFSET_HOURS && hoursDelta < HOURS;
        }
    }
    catch (error) {
        return false;
    }
};
/**
 * Validates if a timezone string is a valid IANA timezone name.
 * Rejects offset formats like "UTC+01:00" or "+01:00" which cannot be used with date-fns-tz.
 *
 * @param timezone - The timezone string to validate
 * @returns true if the timezone is a valid IANA timezone name, false otherwise
 */
const isValidIANATimezone = (timezone) => {
    if (!timezone)
        return false;
    // Reject offset formats like "UTC+01:00", "+01:00", "UTC+1", "+1"
    if (timezone.match(/^(UTC)?[+-]\d{1,2}(:\d{2})?$/)) {
        console.warn(`Invalid timezone format: "${timezone}" appears to be a UTC offset, not an IANA timezone name. ` +
            `Expected format like "Europe/Amsterdam" or "America/New_York"`);
        return false;
    }
    // Valid IANA timezones contain "/" (e.g., "Europe/Amsterdam", "America/New_York")
    // or are short forms like "UTC", "GMT"
    if (timezone === "UTC" || timezone === "GMT") {
        return true;
    }
    if (!timezone.includes("/")) {
        console.warn(`Potentially invalid timezone format: "${timezone}". Expected IANA timezone name like "Europe/Amsterdam"`);
        return false;
    }
    return true;
};
export const getNetworkEdges = (assetData) => {
    const networkEdges = [];
    const edgeMap = new Map();
    const ghostAssetIds = new Set();
    let n = 0;
    assetData.forEach((data) => {
        if (data?.chargingSource) {
            edgeMap.set(data.assetId, data.chargingSource);
        }
        if (isChargingStation(data?.assetSourceType)) {
            edgeMap.set(data.assetId, "Power");
        }
        if (isGhost(data?.assetSourceType) && data?.chargingSource) {
            ghostAssetIds.add(data.assetId);
        }
    });
    for (const [key, value] of edgeMap.entries()) {
        const isParallelConnection = ghostAssetIds.has(key);
        networkEdges.push({
            id: "e" + n,
            source: value,
            style: { strokeWidth: 3 },
            target: key,
            type: isParallelConnection ? "straight" : undefined
        });
        n++;
    }
    return networkEdges;
};
export const getAssetName = (assetName, assetSourceType) => {
    // Handle specific asset name mappings using shared constant
    if (ASSET_NAME_MAPPINGS[assetName]) {
        return ASSET_NAME_MAPPINGS[assetName];
    }
    if (isGhost(assetSourceType)) {
        const unknownFormatted = formatUnknownAssetName(assetName);
        if (unknownFormatted)
            return unknownFormatted;
        return assetName ? assetName.replace("Unknown Machine", "").trim() : "";
    }
    const FIRST_POSITION = 0;
    const MACHINE_LAST_POSITION = 5;
    const COMMON_LAST_POSITION = 3;
    const LAST_POSITION = isMachine(assetSourceType) ? MACHINE_LAST_POSITION : COMMON_LAST_POSITION;
    if (assetName && isChargingStation(assetSourceType)) {
        return assetName
            .replace(/_([a-z])/g, (_, char) => ` ${char.toUpperCase()}`)
            .replace(/^./, (char) => char.toUpperCase());
    }
    if (!assetName)
        return "";
    // Preserve a trailing "-<n>" unit number (e.g. "6TA0083B-2" -> "6TA-2") so multiple
    // units of one model stay distinct and short instead of overflowing the node box.
    const unitSuffix = /^(.*)-(\d{1,3})$/.exec(assetName);
    if (unitSuffix) {
        const [, base, unit] = unitSuffix;
        return `${base.substring(FIRST_POSITION, LAST_POSITION)}-${unit}`;
    }
    return assetName.substring(FIRST_POSITION, LAST_POSITION);
};
export const trimAssetName = (assetName, assetMetadata) => {
    if (!assetName)
        return "";
    // If make is DENS, show first two and last two characters
    if (assetMetadata?.make === DENS_MAKE && assetName.length >= 4) {
        return assetName.substring(0, 2) + assetName.substring(assetName.length - 2);
    }
    const updatedAssetName = assetName.split("/");
    return updatedAssetName.length ? updatedAssetName[0] : assetName;
};
/**
 * Determines if the last reported time is stale (older than 18 hours).
 * NOTE: The API provides timestamps already in the project's local timezone.
 *
 * @param lastReportedTime - Timestamp string in project's local timezone
 * @param projectTimezone - IANA timezone name (e.g., "Europe/London", "Europe/Amsterdam")
 * @returns true if the timestamp is stale or invalid, false otherwise
 */
/**
 * Checks if the last reported time is stale (more than 18 hours ago).
 *
 * CALCULATION FUNCTION - Uses fromZonedTime approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * Similar to isAssetReporting(), this needs accurate UTC milliseconds for comparison.
 * All users must get the same result regardless of their browser's timezone.
 */
export const isLastReportedTimeStale = (lastReportedTime, projectTimezone) => {
    const EIGHTEEN = 18;
    if (!lastReportedTime) {
        return true;
    }
    try {
        // Special handling for time-only format (e.g., "19:00:00")
        // Append today's date in the project's timezone to construct a full timestamp
        if (isTimeOnlyTimestamp(lastReportedTime)) {
            if (!projectTimezone || !isValidIANATimezone(projectTimezone)) {
                // Cannot determine staleness without valid timezone, assume stale
                return true;
            }
            // Get today's date in the project's timezone (e.g., "2025-12-18" in Europe/Amsterdam)
            const todayInProjectTz = formatInTimeZone(new Date(), projectTimezone, "yyyy-MM-dd");
            // Construct full timestamp: "2025-12-18T19:00:00"
            const fullTimestamp = `${todayInProjectTz}T${lastReportedTime}`;
            // Parse as project timezone and convert to UTC for comparison
            const lastReportedDate = fromZonedTime(fullTimestamp, projectTimezone);
            if (isNaN(lastReportedDate.getTime())) {
                return true;
            }
            const currentTime = new Date();
            const hoursDifference = differenceInHours(currentTime, lastReportedDate);
            return hoursDifference > EIGHTEEN;
        }
        // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 London time)
        // We interpret this timestamp AS IF it's in the project's timezone, then convert to UTC for comparison
        let lastReportedDate;
        if (projectTimezone && isValidIANATimezone(projectTimezone)) {
            // Parse the timestamp string as a date in the project's timezone, convert to UTC
            // Returns a Date object with correct UTC milliseconds for reliable comparison
            lastReportedDate = fromZonedTime(lastReportedTime, projectTimezone);
        }
        else {
            // Fallback: treat as UTC if no timezone provided
            const timeAsUTC = lastReportedTime.endsWith("Z") ? lastReportedTime : `${lastReportedTime}Z`;
            lastReportedDate = new Date(timeAsUTC);
        }
        if (isNaN(lastReportedDate.getTime())) {
            return true;
        }
        const currentTime = new Date();
        const hoursDifference = differenceInHours(currentTime, lastReportedDate);
        return hoursDifference > EIGHTEEN;
    }
    catch (error) {
        // If any error occurs during parsing, consider the time as stale
        return true;
    }
};
export const getAssetImageIcon = (assetDetails) => {
    const productFamilyIconUrl = assetDetails?.assetMetadata?.productFamily?.iconUrl || assetDetails?.productFamily?.iconUrl;
    if (productFamilyIconUrl?.trim()) {
        return productFamilyIconUrl.trim();
    }
    if (CAT_950_ASSET_IDS.has(assetDetails?.assetId)) {
        return cat950Icon;
    }
    if (XOS_CHARGER_ASSET_IDS.includes(assetDetails?.assetId)) {
        return xosHubIcon;
    }
    if (assetDetails?.assetMetadata?.make === PSW_MAKE) {
        return pswIcon;
    }
    if (assetDetails?.assetMetadata?.model === MODEL_950GC &&
        assetDetails?.assetMetadata?.productFamily?.code === MDWL_PRODUCT_FAMILY) {
        return MediumWheelLoaderIcon;
    }
    if (isEss(assetDetails?.assetSourceType)) {
        const essIcon = ESS_PRODUCT_FAMILY_ICONS[assetDetails?.assetMetadata?.productFamily?.code];
        if (essIcon)
            return essIcon;
    }
    return ASSET_TYPE_CONFIG[normalizeAssetType(assetDetails?.assetSourceType)]?.icon ?? defaultMarkerIcon;
};
// Serial numbers for XOS Hub chargers that have a dedicated icon
const XOS_CHARGER_SERIAL_NUMBERS = ["010481", "010672"];
/**
 * Icon resolver for map markers. Regular CHARGER assets use the map-specific
 * ChargingStationMapIcon; XOS Hub chargers use the XOS Hub icon. All other
 * asset types delegate to getAssetImageIcon.
 */
export const getAssetMapIcon = (assetDetails) => {
    if (isCharger(assetDetails?.assetSourceType)) {
        if (XOS_CHARGER_SERIAL_NUMBERS.includes(assetDetails?.assetSerialNumber)) {
            return xosHubIcon;
        }
        return chargingStationMapIcon;
    }
    return getAssetImageIcon(assetDetails);
};
const getLabels = (activities, labelToSourceType = {}) => {
    const uniqueLabels = [];
    const chargerLabels = [];
    const fixedLabels = [];
    const activityLabels = [];
    if (!activities || activities.length < 1)
        return [];
    activities.forEach((activity) => {
        const chargeAsset = formatAssetLabelForSankey(activity.assetName);
        const dischargeAsset = formatAssetLabelForSankey(activity.dischargeAssetName);
        if (!uniqueLabels.includes(chargeAsset))
            uniqueLabels.push(chargeAsset);
        if (!uniqueLabels.includes(dischargeAsset))
            uniqueLabels.push(dischargeAsset);
    });
    uniqueLabels.forEach((label) => {
        if (isCharger(labelToSourceType[label])) {
            // CHARGER nodes appear at the top of the left column, above WATTHUB
            chargerLabels.push(label);
        }
        else if (label === "WATTHUB" || label?.includes("Residual")) {
            fixedLabels.push(label);
        }
        else {
            activityLabels.push(label);
        }
    });
    //updatedLabels: CHARGERs first, then WATTHUB/Residual, then the rest
    return chargerLabels.concat(fixedLabels).concat(activityLabels);
};
const getSankeyChartLabels = (activities, labelToSourceType = {}) => {
    const uniqueLabels = [];
    const chargerLabels = [];
    const otherLabels = [];
    if (!activities || activities.length < 1)
        return [];
    activities.forEach((activity) => {
        const chargeAsset = formatAssetLabelForSankey(activity.assetName);
        const dischargeAsset = getSankeyChartDischargeLabel(activity);
        if (!uniqueLabels.includes(chargeAsset))
            uniqueLabels.push(chargeAsset);
        if (!uniqueLabels.includes(dischargeAsset))
            uniqueLabels.push(dischargeAsset);
    });
    // Hoist CHARGER labels before WATTHUB so D3 stacks them at the top of column 0
    uniqueLabels.forEach((label) => {
        if (isCharger(labelToSourceType[label])) {
            chargerLabels.push(label);
        }
        else {
            otherLabels.push(label);
        }
    });
    return chargerLabels.concat(otherLabels);
};
const getLinks = (activities, labels) => {
    if (!activities || activities.length < 1)
        return { source: [], target: [], value: [] };
    const source = activities.map((activity) => labels.findIndex((label) => label === formatAssetLabelForSankey(activity.assetName)));
    const target = activities.map((activity) => labels.findIndex((label) => label === formatAssetLabelForSankey(activity.dischargeAssetName)));
    const value = activities.map((activity) => activity.dischargeEnergy);
    return {
        source,
        target,
        value
    };
};
const getSankeyChartLinks = (activities) => {
    const linkMap = new Map();
    activities.forEach((activity) => {
        const source = formatAssetLabelForSankey(activity.assetName);
        const target = getSankeyChartDischargeLabel(activity);
        const value = activity.dischargeEnergy ? parseFloat(activity.dischargeEnergy) : 0;
        const key = `${source}→${target}`;
        const existing = linkMap.get(key);
        if (existing) {
            existing.value += value;
        }
        else {
            linkMap.set(key, { source, target, value });
        }
    });
    return Array.from(linkMap.values());
};
const getSankeyChartDischargeLabel = (activity) => {
    const dischargeAsset = formatAssetLabelForSankey(activity.dischargeAssetName);
    if ((dischargeAsset === "ESS Residual Energy" || dischargeAsset === "ESS Energy Loss") && activity.assetName) {
        return `${dischargeAsset}::${formatAssetLabelForSankey(activity.assetName)}`;
    }
    return dischargeAsset;
};
const formatAssetLabelForSankey = (label) => {
    // First, check if this asset has a special name mapping (e.g., PH10-24016 -> PH16)
    // This ensures Sankey diagrams show correct names like "PH16" and "PH19" instead of "PH10"
    if (ASSET_NAME_MAPPINGS[label]) {
        return ASSET_NAME_MAPPINGS[label];
    }
    // Preserve a trailing "-<n>" unit number (e.g. "6TA0083B-2") so multiple units of one
    // model aren't collapsed onto the same Sankey node/label.
    if (label && /^(.*)-(\d{1,3})$/.test(label)) {
        return label;
    }
    // Default behavior: extract the prefix before special characters
    let words;
    if (label?.includes("(")) {
        words = label.split("(");
    }
    else {
        words = label?.split("-");
    }
    return words !== undefined ? words[0] : label;
};
const getPositions = (labels, labelToSourceType = {}) => {
    const x = [];
    const y = [];
    if (!labels || labels.length < 1)
        return { x: [], y: [] };
    let cy = 0.1;
    let ey = 0.0;
    let my = 0.1;
    let oy = 0.0;
    labels.map((label) => {
        // To fix any deviations of data, where the node would be rendered too far
        // from its ideal position. For now this is not needed, but leaving this
        // mapping there, if needed in future
        if (isCharger(labelToSourceType[label])) {
            // CHARGER: left column (x=0.0), above WATTHUB (y < 0.5)
            x.push(0.0);
            y.push(cy);
            cy = cy + 0.1;
        }
        else if (label === "WATTHUB" || isChargingStation(labelToSourceType[label])) {
            const wattHubXValue = 0.0;
            const wattHubYValue = 0.5;
            x.push(wattHubXValue);
            y.push(wattHubYValue);
        }
        else if (label.startsWith("1") ||
            label.startsWith("3") ||
            label.startsWith("4") ||
            label.includes("Other Machine") ||
            label.startsWith("Unknown")) {
            const otherMachineXValue = 0.4;
            x.push(otherMachineXValue);
            my = my + 0.1;
            y.push(my);
        }
        else if (label.startsWith("CQ") ||
            label.startsWith("BQ") ||
            label.startsWith("VO") ||
            label.startsWith("RL") ||
            label.startsWith("RO") ||
            label.includes("Other ESS")) {
            const otherESSValue = 0.2;
            x.push(otherESSValue);
            ey = ey + 0.1;
            y.push(ey);
        }
        else {
            x.push(1);
            oy = oy + 0.1;
            y.push(oy);
        }
    });
    return {
        x,
        y
    };
};
const getCustomData = (labels) => labels.map((label) => label.toString());
export const prepareSankeyData = (activities) => {
    const labelToSourceType = {};
    (activities || []).forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType)
            labelToSourceType[formatted] = activity.assetSourceType;
    });
    const labels = getLabels(activities, labelToSourceType);
    const links = getLinks(activities, labels);
    const positions = getPositions(labels, labelToSourceType);
    const customData = getCustomData(labels);
    return {
        customData,
        labels,
        links,
        positions
    };
};
const prepareSankeyChartNodes = (labels, activities = []) => {
    // Build a lookup: formatted label → assetSourceType from the raw activities
    const labelToSourceType = {};
    activities.forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType) {
            labelToSourceType[formatted] = activity.assetSourceType;
        }
    });
    const nodes = [];
    labels.map((label) => {
        // Methanol reformers: identified by label prefix (E_N_) or assetSourceType ENERGY_SOURCE
        if (label.startsWith("E_N_") || isEnergySource(labelToSourceType[label])) {
            nodes.push({ id: label, type: ASSET_TYPE_CONFIG[AssetSourceType.ENERGY_SOURCE].sankeyNodeType });
        }
        else if (isCharger(labelToSourceType[label])) {
            // Classify as CHARGER type so it is mapped to column 0 (same as CHARGING_STATION).
            // Vertical ordering above WATTHUB is controlled by getLabels/getSankeyChartLabels
            // (which hoist CHARGER labels first) and getPositions (which assigns y < 0.5).
            nodes.push({ id: label, type: ASSET_TYPE_CONFIG[AssetSourceType.CHARGER].sankeyNodeType });
        }
        else if (label.toUpperCase() === "WATTHUB" ||
            label === "Office Grid Connection" ||
            label === "Site Grid Connection" ||
            label.toLowerCase() === "unknown" ||
            isChargingStation(labelToSourceType[label])) {
            nodes.push({ id: label, type: ASSET_TYPE_CONFIG[AssetSourceType.CHARGING_STATION].sankeyNodeType });
        }
        else if (label.startsWith("CQ") ||
            label.startsWith("BQ") ||
            label.startsWith("VO") ||
            label.startsWith("RL") ||
            label.startsWith("RO") ||
            label.startsWith("AC") ||
            label.startsWith("DC") ||
            label.startsWith("XES") ||
            label.startsWith("PH") || // Added PH prefix for ESS assets like PH19, PH16
            label.includes("Other ESS")) {
            nodes.push({ id: label, type: "ESS" });
        }
        else if (label.startsWith("1") ||
            label.startsWith("3") ||
            label.startsWith("4") ||
            label.startsWith("9") ||
            label.includes("Other Machine")) {
            nodes.push({ id: label, type: "MACHINE" });
        }
        else if (label?.toLowerCase()?.startsWith("unknown") || isGhost(labelToSourceType[label])) {
            nodes.push({ id: label, type: ASSET_TYPE_CONFIG[AssetSourceType.GHOST].sankeyNodeType });
        }
        else if (label.includes("Residual") || label.includes("Energy Loss")) {
            nodes.push({ id: label, type: "RESIDUAL" });
        }
        else {
            nodes.push({ id: label, type: "OTHER" });
        }
    });
    return nodes;
};
export const prepareSankeyChartData = (activities, showMethanolReformer = false, showMecCharger = false) => {
    const formatAssetName = (name) => {
        if (name.toLowerCase() === "office_grid_connection") {
            return "Office Grid Connection";
        }
        else if (name.toLowerCase() === "site_grid_connection") {
            return "Site Grid Connection";
        }
        else {
            return name;
        }
    };
    activities.forEach((activity) => {
        activity.assetName = formatAssetName(activity.assetName);
        activity.dischargeAssetName = formatAssetName(activity.dischargeAssetName);
    });
    // Build a shared labelToSourceType lookup used by labels, links and node classification
    const labelToSourceType = {};
    activities.forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType)
            labelToSourceType[formatted] = activity.assetSourceType;
    });
    const labels = getSankeyChartLabels(activities, labelToSourceType);
    const links = getSankeyChartLinks(activities);
    // Pass activities so nodes can resolve assetSourceType for ENERGY_SOURCE classification
    const nodes = prepareSankeyChartNodes(labels, activities);
    const filteredNodeList = showMethanolReformer ? nodes : nodes.filter((node) => node.type !== "METHANOL_REFORMER");
    const filteredNodeIdList = new Set(filteredNodeList.map((node) => node.id));
    const filteredLinkList = links.filter((link) => filteredNodeIdList.has(link.source) && filteredNodeIdList.has(link.target));
    // Multiple types share column 0 so D3 stacks them vertically on the left side.
    const TYPE_TO_COLUMN = {
        CHARGER: 0,
        CHARGING_STATION: 0,
        ESS: 1,
        GHOST: 2,
        MACHINE: 2,
        ...(showMethanolReformer ? { METHANOL_REFORMER: 0 } : {}),
        ...(showMecCharger ? { CHARGER: 0 } : {}),
        OTHER: 3,
        RESIDUAL: 2
    };
    const SVG_WIDTH = SANKEY_SVG_WIDTH;
    const NODE_WIDTH = SANKEY_NODE_WIDTH;
    const PADDING = 60;
    const presentColumns = [...new Set(filteredNodeList.map((node) => TYPE_TO_COLUMN[node.type]))].sort((a, b) => a - b);
    const columnCount = presentColumns.length;
    const availableWidth = SVG_WIDTH - 2 * PADDING - NODE_WIDTH;
    const spacing = columnCount > 1 ? availableWidth / (columnCount - 1) : 0;
    filteredNodeList.forEach((node) => {
        const column = TYPE_TO_COLUMN[node.type];
        const columnIndex = presentColumns.indexOf(column);
        node.xPos = PADDING + columnIndex * spacing;
    });
    return {
        links: filteredLinkList,
        nodes: filteredNodeList
    };
};
export const extractHourlyEnergyData = (energyUsageHistory) => {
    const hourlyUsage = [];
    if (energyUsageHistory && energyUsageHistory.energyHistogram && energyUsageHistory.energyHistogram["hourly"]) {
        energyUsageHistory.energyHistogram["hourly"].map((usage) => {
            hourlyUsage.push(Math.round(usage.value));
        });
    }
    return hourlyUsage;
};
export const getLocalTime = (utc, offset) => {
    if (isNotEmpty(utc) && isNotEmpty(offset)) {
        // Below if condition for test site where date format is not in ISO format
        if (isTimeOnlyTimestamp(utc)) {
            utc = new Date().toISOString().split("T")[0] + "T" + utc;
        }
        const words = offset.split("UTC");
        const offsetTime = parseInt(words[1]);
        const utcDate = new Date(utc);
        if (isNaN(utcDate.valueOf())) {
            return null;
        }
        const MILLI_SECONDS = 3600000;
        return new Date(utcDate.getTime() + offsetTime * MILLI_SECONDS);
    }
    return null;
};
function isNotEmpty(str) {
    return typeof str === "string" && str.trim() !== "";
}
export const getSankeyNodeColor = (type) => {
    switch (type) {
        case "ESS":
            return "#CAE5C7";
        case "GHOST":
            return "#FFE672";
        case "MACHINE":
            return "#FFE672";
        case "CHARGING_STATION":
            return "#9E90D5";
        case "CHARGER":
            return "#4DB6E8";
        case "METHANOL_REFORMER":
            // Teal (#1AC69E) — matches Figma spec for Methanol Solution node color
            return "#1AC69E";
        case "RESIDUAL":
            return "#FB5A00";
        default:
            return "#B1E3E3";
    }
};
export const extractDailyEnergyData = (energyUsageHistory) => {
    const formattedChargeEnergy = [], formattedDischargeEnergy = [], formattedDate = [];
    let totalDischarge = 0;
    if (energyUsageHistory &&
        energyUsageHistory.energyHistogram &&
        energyUsageHistory.energyHistogram["dailyWiseMonth"]) {
        energyUsageHistory.energyHistogram["dailyWiseMonth"].forEach((usage) => {
            const { chargeEnergy = 0, date = "", dischargeEnergy = 0 } = usage;
            let formattedDateStr = "";
            try {
                const parsedDate = new Date(date);
                if (isNaN(parsedDate.getTime())) {
                    throw new Error(`Invalid date: ${date}`);
                }
                formattedDateStr = format(parsedDate, "dd-MMM");
            }
            catch (error) {
                console.error("Error formatting date:", error);
                // Fallback: try to extract date parts manually
                if (date && typeof date === "string") {
                    const dateParts = date.split(/[-T]/);
                    if (dateParts.length >= 3) {
                        const dayStr = dateParts[2];
                        const monthStr = dateParts[1];
                        // Validate day and month values
                        const dayNum = parseInt(dayStr, 10);
                        const monthIndex = parseInt(monthStr, 10) - 1;
                        // Check if parsed values are valid numbers within expected ranges
                        const isDayValid = !isNaN(dayNum) && dayNum >= 1 && dayNum <= 31;
                        const isMonthValid = !isNaN(monthIndex) && monthIndex >= 0 && monthIndex <= 11;
                        if (isDayValid && isMonthValid) {
                            const monthName = MONTH_NAMES[monthIndex];
                            formattedDateStr = `${dayStr.padStart(2, "0")}-${monthName}`;
                        }
                        else {
                            // Invalid day or month, fall back to substring
                            formattedDateStr = date.substring(0, 10);
                        }
                    }
                    else {
                        formattedDateStr = date.substring(0, 10);
                    }
                }
                else {
                    formattedDateStr = "N/A";
                }
            }
            formattedChargeEnergy.push(Math.round(chargeEnergy));
            formattedDischargeEnergy.push(Math.round(dischargeEnergy));
            formattedDate.push(formattedDateStr);
            totalDischarge += dischargeEnergy;
        });
    }
    return {
        formattedChargeEnergy,
        formattedDate,
        formattedDischargeEnergy,
        totalDischarge
    };
};
export const extractDailyChargerEnergyData = (energyUsageHistory) => {
    const formattedChargeEnergy = [], formattedDischargeEnergy = [], formattedDate = [];
    let totalDischarge = 0;
    if (energyUsageHistory &&
        energyUsageHistory.energyHistogram &&
        energyUsageHistory.energyHistogram["dailyWiseMonth"]) {
        energyUsageHistory.energyHistogram["dailyWiseMonth"].forEach((usage) => {
            const { chargeEnergy = 0, date = "", dischargeEnergy = 0 } = usage;
            formattedChargeEnergy.push(Math.round(chargeEnergy));
            formattedDischargeEnergy.push(Math.round(dischargeEnergy));
            formattedDate.push(date);
            totalDischarge += dischargeEnergy;
        });
    }
    return {
        formattedChargeEnergy,
        formattedDate,
        formattedDischargeEnergy,
        totalDischarge
    };
};
/**
 * Formats a timestamp string for display.
 * NOTE: The API already provides timestamps in the project's local timezone.
 * This function displays the timestamp AS-IS without timezone conversion.
 *
 * IMPORTANT: For DISPLAY purposes only. For calculations (like determining if an asset
 * is reporting), use isAssetReporting() or isLastReportedTimeStale() which properly
 * interpret the timestamp using fromZonedTime().
 *
 * @param localDate - Timestamp string already in the project's local timezone (e.g., "2025-12-09T11:27:00.000")
 * @param formatStr - Optional format string for date-fns format function (default: "dd MMM yyy HH:mm")
 * @param _projectOffset - Optional offset string (kept for backward compatibility, not used)
 * @param _projectTimezone - Optional timezone name (kept for backward compatibility, not used)
 * @returns Formatted timestamp string, or null if input is invalid
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Formats a timestamp for display in the UI.
 *
 * DISPLAY FUNCTION - Uses Z-Appending approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * NOTE: The API provides timestamps already in the project's local timezone.
 * This function ensures ALL users globally see the SAME wall-clock time.
 *
 * Why Z-appending instead of fromZonedTime:
 *   - We want to show "20:46" to everyone (Tokyo, New York, London users)
 *   - Z-appending prevents browser timezone conversion
 *   - formatInTimeZone with UTC keeps the original time values intact
 *   - fromZonedTime would give us correct UTC, but format() would convert to browser timezone
 *
 * Example:
 *   Input: "2025-12-17T20:46:00.000" (London time)
 *   Tokyo user → sees "20:46 GMT"
 *   New York user → sees "20:46 GMT"
 *   London user → sees "20:46 GMT"
 *
 * @param localDate - Timestamp string in project's local timezone
 * @param formatStr - Optional date format string (default: "dd MMM yyy HH:mm")
 * @param _projectOffset - Deprecated, kept for backward compatibility
 * @param _projectTimezone - Deprecated, kept for backward compatibility
 * @returns Formatted date string, or null if invalid
 */
export const getCESTLocalTime = (localDate, formatStr = "", 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_projectOffset, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_projectTimezone) => {
    if (isNotEmpty(localDate)) {
        // Handle legacy time-only format (HH:mm:ss) by prepending today's date
        // NOTE: Some project API always returns timestamps as time-only format (e.g., "07:00:00")
        // This is a known limitation of the those specific data sources
        // For display, components should detect this format and show "time only" to users
        if (localDate.indexOf("T") === -1 && localDate.indexOf("-") === -1) {
            if (isTimeOnlyTimestamp(localDate)) {
                // Prepend today's date as fallback for formatting purposes
                // Note: This loses actual historical date information
                localDate = new Date().toISOString().split("T")[0] + "T" + localDate + ".000";
            }
        }
        const dateFormat = formatStr ? formatStr : "dd MMM yyy HH:mm";
        try {
            // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 Amsterdam time)
            // For DISPLAY purposes, we want to show this EXACT time to all users (no conversion)
            // We append 'Z' to treat the timestamp as UTC, then format in UTC timezone
            // This prevents any browser timezone conversion while preserving the original time values
            const timeAsUTC = localDate.endsWith("Z") ? localDate : `${localDate}Z`;
            const parsedDate = new Date(timeAsUTC);
            // Check if the date is valid
            if (isNaN(parsedDate.getTime())) {
                return null;
            }
            // Format using formatInTimeZone with UTC to force display without timezone conversion
            // This ensures all users globally see the same time values (the project's local time)
            return formatInTimeZone(parsedDate, "UTC", dateFormat);
        }
        catch (error) {
            console.error("Error formatting date:", error);
            return null;
        }
    }
    return null;
};
/**
 * Formats last reported timestamp with timezone-aware display.
 * Shared helper for pages/drawers that need the AssetPageDetail formatting behavior.
 */
export const formatLastReportedTime = (time, timezone, showCestTimezone) => {
    if (!time)
        return "";
    const { formattedTime, timezoneAbbr } = formatTimestampWithTimezone(time, timezone, "us-date", (ts) => {
        if (showCestTimezone && !ts.endsWith("Z")) {
            try {
                const [datePart, timePart] = ts.split("T");
                if (!datePart || !timePart)
                    return "";
                const [year, month, day] = datePart.split("-");
                const [hour, minute] = timePart.split(":");
                return `${month}/${day}/${year}; ${hour}:${minute}`;
            }
            catch (error) {
                return "";
            }
        }
        return getCESTLocalTime(ts, "MM/dd/yyyy; HH:mm") || "";
    });
    return timezoneAbbr ? `${formattedTime} ${timezoneAbbr}` : formattedTime || "";
};
/**
 * Radius (in degrees) of the circular spread applied when assets share the same
 * coordinates.  0.00005° ≈ 5 metres at the equator.
 */
export const JITTER_RADIUS = 0.00005;
/**
 * Spreads entries that share identical coordinates into a small circle so that
 * every map marker gets a distinct pixel position.
 *
 * Operates only on the `location` field; all other properties are preserved
 * by value.  Entries with unique coordinates are returned as-is (same reference).
 */
export function applyCoordinateJitter(entries) {
    const coordCount = new Map();
    const coordIndex = new Map();
    entries.forEach((d) => {
        const key = `${d.location[0]},${d.location[1]}`;
        coordCount.set(key, (coordCount.get(key) || 0) + 1);
    });
    return entries.map((d) => {
        const key = `${d.location[0]},${d.location[1]}`;
        const total = coordCount.get(key) || 1;
        if (total <= 1)
            return d;
        const idx = coordIndex.get(key) || 0;
        coordIndex.set(key, idx + 1);
        const angle = (2 * Math.PI * idx) / total;
        return {
            ...d,
            location: [d.location[0] + JITTER_RADIUS * Math.cos(angle), d.location[1] + JITTER_RADIUS * Math.sin(angle)]
        };
    });
}
